import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import Loading from '../components/Loading';
import './AnalyticDashboardIndex.css';

/**
 * A "home" do painel do cliente desde 02/09/2026: os dashboards que a Aliado publicou
 * para esta empresa. A rota `/` do subdomínio manda para cá — o Dash Monitor
 * (pages/Home.js) não é mais servido em nenhum tenant.
 *
 * Três caminhos:
 *
 *  - nenhum dashboard publicado -> a tela de "Nenhum dashboard disponível";
 *  - exatamente um -> abre direto, sem obrigar um clique num índice de um item só;
 *  - vários -> a grade.
 *
 * O primeiro caminho ANTES caía no dashboard estático (POST /staticdash). Isso saiu a
 * pedido (02/09/2026): empresa sem dashboard publicado passa a ver a mensagem. O
 * estático continua existindo em AnalyticDashboard sem `:slug`, e é por ele que a
 * Magalu passa — o App.js a renderiza em tela cheia sem passar por este índice.
 *
 * Falha ao listar (inclusive o 403 de quem não tem papel de dashboard) também termina
 * aqui: a lista fica vazia e a tela é a mesma, com uma linha discreta dizendo que a
 * lista não pôde ser carregada — para não fingir "não há dashboards" quando o que
 * houve foi um erro.
 *
 * A empresa nunca é escolhida aqui: o servidor a resolve pelo Host.
 */

//: O slug reservado do índice customizado. Espelha `comum/indice.py` no aliado-mcp —
//: não há módulo compartilhado entre Python e JS, então o valor é combinado nos dois
//: lados. Ele é INALCANÇÁVEL para um dashboard publicado (o `_valida_slug` das tools só
//: aceita [a-z0-9-]), que é justamente o que permite servi-lo pelas rotas de sempre.
const SLUG_INDICE = '__indice__';

//: Quanto esperamos o índice customizado carregar antes de desistir dele.
//: Um iframe não avisa quando o servidor devolve erro — `onError` não dispara para
//: status HTTP —, então a única queda confiável é o relógio.
const ESPERA_INDICE_MS = 8000;

//: Ícone derivado do assunto, para quando quem publicou não escolheu um.
//: A ordem importa: a primeira regra que casar vence, então o específico vem antes
//: do genérico ("conversão" antes de "venda", que é substring de vários títulos).
const ICONES_POR_ASSUNTO = [
    [/convers|fechament/i, '💰'],
    [/placar|meta|ranking|pontua|pontos/i, '🏆'],
    [/objec|objeç/i, '🧱'],
    [/funil/i, '🔻'],
    [/comportament|pilar/i, '🧭'],
    [/atendiment|gravaç|escuta/i, '🎧'],
    [/loja|unidade|filial|regiona/i, '🏬'],
    [/vendedor|consultor|equipe|time|pessoa/i, '👥'],
    [/produto|sortiment|catálog|catalog/i, '📦'],
    [/tempo|duraç|hora/i, '⏱️'],
    [/nota|avalia|qualidade/i, '⭐'],
    [/feedback|coment/i, '💬'],
    [/treinament|onboarding|aprendiz/i, '🎓'],
    [/custo|financ|receita/i, '💵'],
    [/evolu|tendênc|tendenc|série|serie|diári|diari|semana|mês|mes\b/i, '📈'],
];

/**
 * O emoji do cartão. Quem publica escolhe em `icone`; se não escolheu, deriva-se do
 * título. Emoji, e não biblioteca de ícones: já é o vocabulário do menu (Sidebar.js),
 * vem colorido, e não acrescenta dependência nem requisição.
 */
const iconeDe = (d) => {
    if (d.icone && String(d.icone).trim()) return String(d.icone).trim();
    const titulo = d.title || '';
    const achou = ICONES_POR_ASSUNTO.find(([re]) => re.test(titulo));
    return achou ? achou[1] : '📊';
};

/** "Caique Leclerc" -> "Caique". Só o primeiro nome: o cartão é estreito. */
const primeiroNome = (nome) => {
    const limpo = String(nome || '').trim();
    if (!limpo) return null;
    return limpo.split(/\s+/)[0];
};

/**
 * Os bullets do cartão. `destaques` é escrito para o cliente final ler; `notes` é o
 * recado técnico de quem publica. Quando não há destaques — dashboards anteriores ao
 * campo — mostra-se o `notes` como parágrafo curto, cortado por CSS. Nunca os dois.
 */
const bulletsDe = (d) => (Array.isArray(d.destaques) ? d.destaques.filter(Boolean).slice(0, 3) : []);

/** O endereço do dashboard: `/dash/<url_path>`, ou `/dash/<slug>` quando não há um. */
const enderecoDe = (d) => `/dash/${encodeURIComponent(d.url_path || d.slug)}`;

const AnalyticDashboardIndex = () => {
    const navigate = useNavigate();
    const [dashboards, setDashboards] = useState(null);
    const [erro, setErro] = useState(null);
    // O dashboard aguardando confirmação de remoção. `null` = nenhum popup aberto.
    const [aRemover, setARemover] = useState(null);
    const [removendo, setRemovendo] = useState(false);
    const [erroRemocao, setErroRemocao] = useState(null);
    // O relatório do que a remoção levou junto. Apagar um dashboard desliga os
    // endpoints que só existiam para ele e revoga os tokens deles — se alguém
    // consumia esses dados por fora, para agora. Fechar o popup em silêncio
    // deixaria isso para descobrirem semanas depois, sem ligação com a causa.
    const [resultado, setResultado] = useState(null);
    const [aposFechar, setAposFechar] = useState(null);
    // O índice customizado: `null` enquanto não sabemos, `{existe:false}` quando a
    // empresa usa a grade padrão. `indiceFalhou` é a queda: se o HTML não carregar,
    // a tela mostra a grade em vez de um retângulo em branco. O cliente talvez veja um
    // layout diferente do combinado; não vê uma porta trancada.
    const [indice, setIndice] = useState(null);
    const [indiceFalhou, setIndiceFalhou] = useState(false);
    const [indiceCarregou, setIndiceCarregou] = useState(false);

    const carregar = React.useCallback(() => {
        return apiService.listDashboards()
            .then((lista) => { setDashboards(lista); return lista; });
    }, []);

    const confirmarRemocao = async () => {
        if (!aRemover) return;
        setRemovendo(true);
        setErroRemocao(null);
        try {
            const res = await apiService.removeDashboard(aRemover.url_path || aRemover.slug);
            const lista = await carregar();
            // Sobrou um só: o índice deixa de fazer sentido e abrimos direto, que é a
            // mesma regra do carregamento normal.
            const destino = lista.length === 1 ? enderecoDe(lista[0]) : null;

            const mexeu = (res?.endpoints_removidos?.length || 0)
                + (res?.endpoints_mantidos?.length || 0);
            if (mexeu) {
                setResultado(res);
                setAposFechar(destino);
            } else {
                setARemover(null);
                if (destino) navigate(destino, { replace: true });
            }
        } catch (e) {
            setErroRemocao(e.message);
        } finally {
            setRemovendo(false);
        }
    };

    const fecharRelatorio = () => {
        const destino = aposFechar;
        setResultado(null);
        setAposFechar(null);
        setARemover(null);
        if (destino) navigate(destino, { replace: true });
    };

    useEffect(() => {
        let cancelado = false;
        // As duas perguntas saem juntas: a lista e "existe índice próprio?". Em série
        // seriam dois tempos de rede empilhados na abertura da tela, e a segunda não
        // depende da primeira. `getIndiceCustomizado` nunca lança.
        Promise.all([apiService.listDashboards(), apiService.getIndiceCustomizado()])
            .then(([lista, ix]) => {
                if (cancelado) return;
                setIndice(ix);
                // O atalho de "um dashboard só abre direto" NÃO vale quando existe índice
                // próprio: alguém desenhou aquela tela de propósito, e pular por ela seria
                // desfazer a decisão em nome de economizar um clique.
                if (!ix.existe && lista.length === 1) {
                    navigate(enderecoDe(lista[0]), { replace: true });
                    return;
                }
                setDashboards(lista);
            })
            .catch((e) => {
                if (cancelado) return;
                // Sem lista não há o que oferecer: cai no estado vazio, que registra o
                // erro numa linha à parte em vez de sumir com ele.
                console.error('Erro ao listar dashboards:', e);
                setErro(e.message);
                setIndice({ existe: false });
                setDashboards([]);
            });
        return () => { cancelado = true; };
    }, [navigate]);

    // A queda do índice customizado, por relógio: arma quando há índice para esperar e
    // desarma no `onLoad` do iframe. Um iframe não avisa erro de status, então "não
    // carregou a tempo" é o único sinal de falha que dá para observar de fora.
    useEffect(() => {
        if (!indice?.existe || indiceCarregou || indiceFalhou) return undefined;
        const t = setTimeout(() => setIndiceFalhou(true), ESPERA_INDICE_MS);
        return () => clearTimeout(t);
    }, [indice, indiceCarregou, indiceFalhou]);

    if (dashboards === null || indice === null) {
        return <Loading message="Carregando dashboards..." />;
    }

    // A tela que a Aliado desenhou para esta empresa, no lugar da grade padrão.
    //
    // Vem ANTES do estado vazio de propósito: um índice pode existir para uma empresa
    // com poucos (ou nenhum) dashboard, e quem o escreveu decide o que dizer nesse caso.
    // A exceção é `erro`: se nem a lista carregou, o `./data/lista` de dentro do iframe
    // também não carregaria, e a mensagem honesta é a de falha.
    //
    // `allow-top-navigation-by-user-activation` é a única permissão que este iframe tem a
    // mais que a dos dashboards, e é o que faz `<a target="_parent">` abrir um dashboard.
    // Ela permite navegação disparada por CLIQUE e não por script — um índice não
    // consegue redirecionar a janela sozinho.
    if (indice.existe && !indiceFalhou && !erro) {
        return (
            <div className="ad-index-custom">
                <iframe
                    title="Dashboard Analítico"
                    src={apiService.dashboardHtmlUrl(SLUG_INDICE)}
                    className="ad-index-custom-frame"
                    onLoad={() => setIndiceCarregou(true)}
                    sandbox="allow-scripts allow-same-origin allow-popups allow-top-navigation-by-user-activation"
                />
            </div>
        );
    }

    // Nenhum dashboard publicado para esta empresa (ou não deu para listar).
    if (dashboards.length === 0) {
        return (
            <div className="ad-index ad-index-vazio">
                <p className="ad-index-vazio-msg">Nenhum dashboard disponível</p>
                {erro && (
                    <p className="ad-index-vazio-erro">
                        Não foi possível carregar a lista de dashboards agora.
                    </p>
                )}
            </div>
        );
    }

    const dataDe = (d) => {
        const iso = d.version_created_at || d.created_at;
        if (!iso) return null;
        try {
            return new Date(iso).toLocaleDateString('pt-BR',
                { day: '2-digit', month: 'short', year: 'numeric' });
        } catch (e) {
            return null;
        }
    };

    return (
        <div className="ad-index">
            <header className="ad-index-cabecalho">
                <h1>Dashboard Analítico</h1>
                <p>{dashboards.length} dashboards disponíveis</p>
            </header>

            {erro && (
                <div className="ad-index-aviso">
                    Não foi possível atualizar a lista agora. Mostrando o que temos.
                </div>
            )}

            <ul className="ad-index-grade">
                {dashboards.map((d) => {
                    const bullets = bulletsDe(d);
                    const autor = primeiroNome(d.author_name);
                    return (
                        <li key={d.slug}>
                            {/* A lixeira é IRMÃ do cartão, não filha: o cartão já é um
                                <button>, e botão dentro de botão é HTML inválido —
                                o clique interno viraria loteria entre os dois. */}
                            {d.pode_remover && (
                                <button
                                    type="button"
                                    className="ad-index-lixeira"
                                    title={`Remover "${d.title}"`}
                                    aria-label={`Remover ${d.title}`}
                                    onClick={() => { setErroRemocao(null); setARemover(d); }}
                                >
                                    🗑️
                                </button>
                            )}
                            <button
                                type="button"
                                className="ad-index-card"
                                onClick={() => navigate(enderecoDe(d))}
                            >
                                <span className="ad-index-topo">
                                    <span className="ad-index-icone" aria-hidden="true">{iconeDe(d)}</span>
                                    <span className="ad-index-titulo">{d.title}</span>
                                </span>

                                <span className="ad-index-meta">
                                    {dataDe(d) && <span>{dataDe(d)}</span>}
                                    {d.version > 1 && <span>versão {d.version}</span>}
                                    {d.status === 'draft' && (
                                        <span className="ad-index-rascunho">rascunho</span>
                                    )}
                                </span>

                                {/* O subtítulo só aparece quando há autor: inventar
                                    "por alguém" seria pior do que omitir a linha. */}
                                {autor && (
                                    <span className="ad-index-autor">
                                        Últimas alterações feitas por {autor}
                                    </span>
                                )}

                                {bullets.length > 0 ? (
                                    <ul className="ad-index-bullets">
                                        {bullets.map((b, i) => <li key={i}>{b}</li>)}
                                    </ul>
                                ) : (
                                    d.notes && <span className="ad-index-notas">{d.notes}</span>
                                )}
                            </button>
                        </li>
                    );
                })}
            </ul>

            {aRemover && (
                <div
                    className="ad-modal-fundo"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="ad-modal-titulo"
                    onClick={(ev) => {
                        if (ev.target !== ev.currentTarget || removendo) return;
                        if (resultado) fecharRelatorio(); else setARemover(null);
                    }}
                >
                    <div className="ad-modal">
                        {resultado ? (
                            <>
                                <h2 id="ad-modal-titulo">Dashboard removido</h2>
                                <p className="ad-modal-alvo">
                                    {iconeDe(aRemover)} <strong>{aRemover.title}</strong>
                                </p>
                                {resultado.endpoints_removidos?.length > 0 && (
                                    <div className="ad-modal-relatorio">
                                        <h3>Endpoints desligados</h3>
                                        <ul>
                                            {resultado.endpoints_removidos.map((e) => (
                                                <li key={e.endpoint_slug}>
                                                    <code>{e.endpoint_slug}</code>
                                                    {e.tokens_revogados > 0
                                                        && ` — ${e.tokens_revogados} token(s) revogado(s)`}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {resultado.endpoints_mantidos?.length > 0 && (
                                    <div className="ad-modal-relatorio">
                                        <h3>Continuam no ar</h3>
                                        <ul>
                                            {resultado.endpoints_mantidos.map((e) => (
                                                <li key={e.endpoint_slug}>
                                                    <code>{e.endpoint_slug}</code> — {e.motivo}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div className="ad-modal-acoes">
                                    <button
                                        type="button"
                                        className="ad-btn-secundario"
                                        onClick={fecharRelatorio}
                                    >
                                        Fechar
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h2 id="ad-modal-titulo">Remover este dashboard?</h2>
                                <p className="ad-modal-alvo">
                                    {iconeDe(aRemover)} <strong>{aRemover.title}</strong>
                                </p>
                                <p className="ad-modal-texto">
                                    Ele sai do índice e o endereço para de responder. O histórico
                                    de versões continua guardado. Os endpoints que só existiam
                                    para este dashboard são desligados junto, e os tokens deles
                                    revogados — quem os consumisse por fora para de receber.
                                </p>
                                {erroRemocao && <p className="ad-modal-erro">{erroRemocao}</p>}
                                <div className="ad-modal-acoes">
                                    <button
                                        type="button"
                                        className="ad-btn-secundario"
                                        onClick={() => setARemover(null)}
                                        disabled={removendo}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        className="ad-btn-perigo"
                                        onClick={confirmarRemocao}
                                        disabled={removendo}
                                    >
                                        {removendo ? 'Removendo...' : 'Remover'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnalyticDashboardIndex;
